import { PIPELINE_OPS_MAX_WINDOW_DAYS } from '@cadstrom/cadstrom-shared';
import { z } from 'zod';

/**
 * The page's URL state: what to read, what to include, and which build's detail is open.
 *
 * All of it lives in the URL rather than in component state so a pasted link lands on the
 * same view, and so changing one does not close the other. The three `include` flags are
 * absent by default and read as false, which is what makes the default link the narrow
 * view: customer work, on the current revision, that something has touched lately.
 */
/**
 * A flag as a query string carries it.
 *
 * Spelled out rather than coerced: `Boolean('false')` is true, so a coerced flag can only
 * ever be turned on, and a link someone edited by hand to `includeInternal=false` would
 * quietly mean the opposite of what it says.
 */
const urlFlag = z
	.union([z.boolean(), z.literal('true'), z.literal('false')])
	.transform((value) => value === true || value === 'true')
	.optional()
	.catch(undefined);

export const searchSchema = z.object({
	/**
	 * How far back to look for settled builds.
	 *
	 * Coerced because search values arrive as strings: the picker writes a number, the URL
	 * carries `windowDays=7`, and it reaches the schema as `"7"`. `catch` then keeps a
	 * hand-edited or out-of-range value from replacing the page with an error boundary —
	 * falling back to the default window is always a better answer than a stack trace.
	 */
	windowDays: z.coerce.number().int().min(1).max(PIPELINE_OPS_MAX_WINDOW_DAYS).optional().catch(undefined),
	/** Open the detail on this build. */
	buildId: z.string().optional(),
	/**
	 * Show only this organization's builds.
	 *
	 * Minimum length, so a `?orgId=` left empty by hand or by a half-finished edit reads as
	 * "every organization" rather than being sent on as an organization whose id is the
	 * empty string, which the endpoint rejects and the page renders as an error boundary.
	 */
	orgId: z.string().min(1).optional().catch(undefined),
	/**
	 * Narrow the grid to the rows answering to this text.
	 *
	 * Trimmed first, so a value of spaces alone reads as no search rather than as one that
	 * matches everything while the page reports a count.
	 */
	q: z
		.string()
		.transform((value) => value.trim())
		.pipe(z.string().min(1))
		.optional()
		.catch(undefined),
	/** Show our own organizations' builds alongside the customers'. */
	includeInternal: urlFlag,
	/** Show builds a later revision has taken over. */
	includeSuperseded: urlFlag,
	/** Show builds nothing has touched for longer than the window. */
	includeDormant: urlFlag,
	/** Show builds delivered within the window. */
	includeDelivered: urlFlag,
});
